/** Small hooks used across domains. */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/** useLayoutEffect on the client, useEffect during SSR/tests where layout does not exist. */
export const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/** A value that only updates after it has been stable for `delay` ms. */
export function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** A ref that always holds the latest value — for callbacks inside long-lived effects. */
export function useLatest<T>(value: T) {
  const ref = useRef(value);
  useIsomorphicLayoutEffect(() => {
    ref.current = value;
  });
  return ref;
}

/**
 * Fullscreen for one element — the element itself, not a `<video>` inside it, so whatever is
 * layered over the video (the caption overlay) goes fullscreen with it. Browsers that cannot
 * fullscreen an arbitrary element (iOS Safari) get a fixed, viewport-filling layout instead; the
 * caller applies `pseudo` as classes.
 */
export function useFullscreen(ref: React.RefObject<HTMLElement | null>) {
  const [native, setNative] = useState(false);
  const [pseudo, setPseudo] = useState(false);

  useEffect(() => {
    const sync = () => setNative(document.fullscreenElement !== null && document.fullscreenElement === ref.current);
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, [ref]);

  useEffect(() => {
    if (!pseudo) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPseudo(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pseudo]);

  const toggle = useCallback(async () => {
    const element = ref.current;
    if (!element) return;
    if (document.fullscreenElement === element) {
      await document.exitFullscreen();
      return;
    }
    if (pseudo) {
      setPseudo(false);
      return;
    }
    if (typeof element.requestFullscreen === 'function') {
      try {
        await element.requestFullscreen();
        return;
      } catch {
        // Refused (permissions policy, or not from a user gesture): use the in-page layout.
      }
    }
    setPseudo(true);
  }, [ref, pseudo]);

  return { active: native || pseudo, pseudo, toggle };
}

/** Track a CSS media query. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false,
  );

  useEffect(() => {
    const list = window.matchMedia(query);
    const handler = (event: MediaQueryListEvent) => setMatches(event.matches);
    setMatches(list.matches);
    list.addEventListener('change', handler);
    return () => list.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

/** The three breakpoints the Blueprint defines. */
export function useBreakpoint() {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1279px)');
  const isDesktop = useMediaQuery('(min-width: 1280px)');
  return { isMobile, isTablet, isDesktop };
}

/** localStorage-backed state that degrades gracefully when storage is unavailable. */
export function useLocalStorage<T>(key: string, initial: T): [T, (value: T) => void] {
  const [stored, setStored] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? initial : (JSON.parse(raw) as T);
    } catch {
      return initial;
    }
  });

  const set = useCallback(
    (value: T) => {
      setStored(value);
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch {
        /* private mode — keep the in-memory value */
      }
    },
    [key],
  );

  return [stored, set];
}

/** Warn before unload while `when` is true. */
export function useBeforeUnload(when: boolean, message: string): void {
  useEffect(() => {
    if (!when) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = message;
      return message;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [when, message]);
}

/** Is the browser online? Drives the offline banner. */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);
  return online;
}

/** Copy to clipboard, reporting success for ~2s so the button can say "Copied". */
export function useCopyToClipboard(): [boolean, (text: string) => Promise<void>] {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard API needs a secure context; fall back to the old selection trick.
      const area = document.createElement('textarea');
      area.value = text;
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      document.execCommand('copy');
      area.remove();
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }, []);

  return [copied, copy];
}

/** Run a handler on a keydown that matches, ignoring events inside text fields. */
export function useHotkey(
  keys: string | string[],
  handler: (event: KeyboardEvent) => void,
  options: { enabled?: boolean; allowInInputs?: boolean } = {},
): void {
  const { enabled = true, allowInInputs = false } = options;
  const handlerRef = useLatest(handler);

  useEffect(() => {
    if (!enabled) return;
    const list = Array.isArray(keys) ? keys : [keys];

    const onKeyDown = (event: KeyboardEvent) => {
      if (!allowInInputs) {
        const target = event.target as HTMLElement | null;
        const tag = target?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
      }
      const combo = [
        event.metaKey || event.ctrlKey ? 'mod' : '',
        event.shiftKey ? 'shift' : '',
        event.altKey ? 'alt' : '',
        event.key.toLowerCase(),
      ]
        .filter(Boolean)
        .join('+');

      if (list.some((k) => k.toLowerCase() === combo || k.toLowerCase() === event.key.toLowerCase())) {
        handlerRef.current(event);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [keys, enabled, allowInInputs, handlerRef]);
}
